import {
  Box,
  CircularProgress,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Text,
  VStack,
  HStack,
  Progress,
  Skeleton,
  useColorModeValue,
  Portal,
  useToast,
  Button,
  Divider,
} from '@chakra-ui/react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { faCoins } from '@fortawesome/free-solid-svg-icons';
import { useCredits, useCreditPacks } from 'src/hooks/credits';
import { InsufficientCreditsModal } from 'src/components/common/InsufficientCreditsModal';
import SparkelIcon from 'src/components/common/icon/SparkelIcon';

type CreditsIndicatorProps = {
  projectId?: string;
};

// eslint-disable-next-line complexity
export function CreditsIndicator({ projectId }: CreditsIndicatorProps) {
  const { t } = useTranslation('header');
  const toast = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const creditsInfo = useCredits(projectId);
  const hasCheckoutSuccess = searchParams.get('checkout_success') === 'true';
  // Use network-only when returning from checkout to ensure fresh data
  // Cache invalidation would work, but after external redirect queries might not be active yet
  const { creditPacks } = useCreditPacks(
    hasCheckoutSuccess ? 'network-only' : 'cache-first'
  );
  const progressColor = useColorModeValue('purple.500', 'purple.300');
  const trackColor = useColorModeValue('gray.200', 'gray.700');
  const [isCreditsModalOpen, setIsCreditsModalOpen] = useState(false);
  const subtitleColor = useColorModeValue('gray.600', 'gray.400');

  // Track which threshold toasts have been shown to avoid duplicates
  const shownThresholdsRef = useRef<Set<number>>(new Set());

  // Listen for checkout success messages from checkout tab
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      // Verify origin for security
      if (event.origin !== window.location.origin) {
        return;
      }

      // Only respond to messages from checkout tabs (not from other sources)
      if (
        event.data?.type === 'CHECKOUT_SUCCESS' &&
        event.data?.source === 'checkout-tab'
      ) {
        // Only process if we don't have an opener (we're the original tab, not a popup)
        // This prevents other tabs from responding
        if (!window.opener || window.opener.closed) {
          // Add checkout_success param to URL before refreshing so toast shows
          const url = new URL(window.location.href);
          url.searchParams.set('checkout_success', 'true');
          window.history.replaceState({}, '', url.toString());

          // Refresh the page to show updated credits and trigger toast
          window.location.reload();
        }
      }
    };

    window.addEventListener('message', handleMessage);
    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, []);

  // Handle checkout success - detect if we're in a checkout tab and close it
  useEffect(() => {
    if (hasCheckoutSuccess) {
      // Check if we're in a popup/checkout tab (opened via window.open)
      // Only close if we have an opener AND we're not the main/original tab
      // We can detect this by checking if window.opener exists and is not closed
      const isCheckoutTab =
        window.opener && !window.opener.closed && window.opener !== window;

      if (isCheckoutTab) {
        // Send message to opener tab to refresh
        // Include a unique identifier to ensure only the intended tab responds
        window.opener.postMessage(
          {
            type: 'CHECKOUT_SUCCESS',
            source: 'checkout-tab',
            timestamp: Date.now(),
          },
          window.location.origin
        );

        // Small delay to ensure message is sent before closing
        setTimeout(() => {
          // Only close if we're still the checkout tab (safety check)
          if (window.opener && !window.opener.closed) {
            window.close();
          }
        }, 100);
        return;
      }

      // Otherwise, we're in the original tab - show toast
      // Use Chakra's built-in duplicate prevention with a unique ID
      const toastId = 'checkout-success';
      if (!toast.isActive(toastId)) {
        toast({
          id: toastId,
          title: t('credits.checkout-success-title', {
            defaultValue: 'Payment successful!',
          }),
          description: t('credits.checkout-success-description', {
            defaultValue: 'Your credits will be available shortly.',
          }),
          status: 'success',
          duration: 6000,
          isClosable: true,
          position: 'top',
        });
      }

      // Remove the query param so it doesn't show again on refresh
      searchParams.delete('checkout_success');
      setSearchParams(searchParams, { replace: true });
    }
  }, [hasCheckoutSuccess, searchParams, setSearchParams, toast, t]); // eslint-disable-line react-hooks/exhaustive-deps

  // Calculate totals including only ACTIVE credit packs (not expired/used)
  // Credit pack expiry rules:
  //   - Packs expire exactly 12 months after purchase
  //   - Expired packs (expiresAt < now) are excluded from available credit totals
  //   - Used packs (availableCredits === 0) are also excluded from totals
  //   - Only active packs (availableCredits > 0 && expiresAt >= now) count toward totals
  // This matches backend logic: expired packs are excluded from credit calculations
  const activePacks = creditPacks.filter((pack) => {
    const now = new Date();
    const expiresAt = new Date(pack.expiresAt);
    return pack.availableCredits > 0 && expiresAt >= now;
  });

  const activePacksTotal = activePacks.reduce(
    (sum, pack) => sum + pack.credits,
    0
  );
  const activePacksUsed = activePacks.reduce(
    (sum, pack) => sum + pack.usedCredits,
    0
  );

  // For circular progress, always include active credit packs only
  const totalWithPacks = creditsInfo.total + activePacksTotal;
  const usedWithPacks = creditsInfo.used + activePacksUsed;

  // Percentage for circular progress (includes credit packs)
  const usedPercentage =
    totalWithPacks > 0 ? Math.round((usedWithPacks / totalWithPacks) * 100) : 0;

  // Percentage for subscription-only progress bar (excludes credit packs)
  const subscriptionUsedPercentage =
    creditsInfo.total > 0
      ? Math.round((creditsInfo.used / creditsInfo.total) * 100)
      : 0;

  // Show toast notifications at 80% and 90% usage thresholds
  useEffect(() => {
    if (creditsInfo.loading || creditsInfo.error || creditsInfo.unlimited) {
      return;
    }

    const thresholds = [
      { percent: 90, status: 'warning' as const },
      { percent: 80, status: 'info' as const },
    ];

    for (const { percent, status } of thresholds) {
      if (
        usedPercentage >= percent &&
        !shownThresholdsRef.current.has(percent)
      ) {
        shownThresholdsRef.current.add(percent);
        if (
          !toast.isActive('credits-threshold-toast') &&
          usedPercentage < 100
        ) {
          toast({
            id: 'credits-threshold-toast',
            title: t('credits.threshold-title', { percent }),
            description: creditsInfo.isProjectScoped
              ? t('credits.threshold-description-project', {
                  used: creditsInfo.used,
                  total: creditsInfo.total,
                })
              : t('credits.threshold-description-tenant', {
                  used: creditsInfo.used,
                  total: creditsInfo.total,
                }),
            status,
            duration: null,
            isClosable: true,
            position: 'top',
            variant: 'solid',
          });
        }
      }
    }
  }, [
    usedPercentage,
    creditsInfo.loading,
    creditsInfo.error,
    creditsInfo.unlimited,
    creditsInfo.isProjectScoped,
    creditsInfo.used,
    creditsInfo.total,
    creditPacks,
    projectId,
    toast,
    t,
  ]);

  if (creditsInfo.loading) {
    return <Skeleton width="40px" height="40px" borderRadius="full" />;
  }

  // Hide indicator if there's an error, unlimited credits, or no credits at all (including packs)

  if (
    creditsInfo.error ||
    creditsInfo.unlimited ||
    (totalWithPacks === 0 && activePacksTotal === 0)
  ) {
    return null;
  }

  return (
    <>
      <Popover trigger="hover" placement="bottom-end">
        <PopoverTrigger>
          <Box cursor="pointer" padding={1}>
            <CircularProgress
              value={usedPercentage}
              color={progressColor}
              trackColor={trackColor}
              size="24px"
              thickness="16px"
            />
          </Box>
        </PopoverTrigger>
        <Portal containerRef={{ current: document.body }}>
          <Box position="relative" zIndex={9999}>
            <PopoverContent width="280px">
              <PopoverArrow />
              <PopoverBody>
                <VStack spacing={3} alignItems="stretch">
                  {creditsInfo.isProjectScoped ? (
                    <Box>
                      <HStack justifyContent="space-between" marginBottom={1}>
                        <Text fontSize="sm" fontWeight="medium">
                          {t('credits.project-credits')}
                        </Text>
                        <Text fontSize="sm" color="gray.600">
                          {creditsInfo.used} / {creditsInfo.total}
                        </Text>
                      </HStack>
                      <Progress
                        value={subscriptionUsedPercentage}
                        colorScheme={'purple'}
                        size="sm"
                        borderRadius="full"
                      />
                      <Text fontSize="xs" color="gray.500" marginTop={1}>
                        {t('credits.available-count', {
                          count: creditsInfo.available,
                        })}
                      </Text>
                    </Box>
                  ) : (
                    <Box>
                      <HStack justifyContent="space-between" marginBottom={1}>
                        <Text fontSize="sm" fontWeight="medium">
                          {t('credits.subscription', {
                            defaultValue: 'Your subscription',
                          })}
                        </Text>
                        <Text fontSize="sm" color="gray.600">
                          {creditsInfo.used} / {creditsInfo.total}
                        </Text>
                      </HStack>
                      <Progress
                        value={subscriptionUsedPercentage}
                        colorScheme={'purple'}
                        size="sm"
                        borderRadius="full"
                      />
                      <Text fontSize="xs" color="gray.500" marginTop={1}>
                        {t('credits.available-count', {
                          count: creditsInfo.available,
                        })}
                      </Text>
                    </Box>
                  )}

                  {creditsInfo.renewalDate &&
                    creditsInfo.renewalAmount !== undefined && (
                      <Box>
                        <VStack spacing={1} align="stretch">
                          <Text
                            fontSize="xs"
                            fontWeight="medium"
                            color={subtitleColor}
                          >
                            {t('credits.next-renewal', {
                              defaultValue: 'Next renewal',
                            })}
                          </Text>
                          <HStack justifyContent="space-between">
                            <Text fontSize="xs" color={subtitleColor}>
                              {new Date(
                                creditsInfo.renewalDate
                              ).toLocaleDateString(undefined, {
                                year: 'numeric',
                                month: 'short',
                                day: 'numeric',
                              })}
                            </Text>
                            <Text fontSize="xs" fontWeight="medium">
                              {creditsInfo.renewalAmount}{' '}
                              {t('credits.credits', {
                                defaultValue: 'credits',
                              })}
                            </Text>
                          </HStack>
                        </VStack>
                      </Box>
                    )}

                  {creditPacks.length > 0 &&
                    creditPacks
                      .filter((pack) => pack.availableCredits > 0)
                      .map((pack) => {
                        const packUsedPercentage =
                          pack.credits > 0
                            ? Math.round(
                                (pack.usedCredits / pack.credits) * 100
                              )
                            : 0;
                        return (
                          <Box key={pack.id}>
                            <Divider marginBottom={2} />
                            <HStack
                              justifyContent="space-between"
                              marginBottom={1}
                            >
                              <Text fontSize="sm" fontWeight="medium">
                                {t('credits.credit-pack', {
                                  defaultValue: 'Credit Pack',
                                })}
                              </Text>
                              <Text fontSize="sm" color="gray.600">
                                {pack.usedCredits} / {pack.credits}
                              </Text>
                            </HStack>
                            <Progress
                              value={packUsedPercentage}
                              colorScheme="teal"
                              size="sm"
                              borderRadius="full"
                            />
                            <Text fontSize="xs" color="gray.500" marginTop={1}>
                              {t('credits.available-count', {
                                count: pack.availableCredits,
                              })}
                            </Text>
                            <Text
                              fontSize="xs"
                              color={subtitleColor}
                              marginTop={1}
                            >
                              {t('credits.expires', {
                                defaultValue: 'Expires',
                              })}{' '}
                              {new Date(pack.expiresAt).toLocaleDateString(
                                undefined,
                                {
                                  year: 'numeric',
                                  month: 'short',
                                  day: 'numeric',
                                }
                              )}
                            </Text>
                          </Box>
                        );
                      })}

                  <Divider />

                  <Button
                    size="xs"
                    borderRadius="md"
                    colorScheme="orange"
                    variant="outline"
                    width="100%"
                    leftIcon={<SparkelIcon icon={faCoins} color="inherit" />}
                    onClick={() => setIsCreditsModalOpen(true)}
                  >
                    {t('credits.add-credits', { defaultValue: 'Add credits' })}
                  </Button>
                </VStack>
              </PopoverBody>
            </PopoverContent>
          </Box>
        </Portal>
      </Popover>
      {isCreditsModalOpen && (
        <InsufficientCreditsModal
          isOpen={isCreditsModalOpen}
          onClose={() => setIsCreditsModalOpen(false)}
          creditsNeeded={0}
          creditsAvailable={creditsInfo.available}
          isProjectScoped={creditsInfo.isProjectScoped}
          renewalDate={creditsInfo.renewalDate}
          renewalAmount={creditsInfo.renewalAmount}
        />
      )}
    </>
  );
}

export default CreditsIndicator;
